import { useMemo } from 'react';
import type { FC } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { changeComposeExpiration } from '@/mastodon/actions/compose';
import { Dropdown } from '@/mastodon/components/dropdown_menu';
import { IconButton } from '@/mastodon/components/icon_button';
import type { MenuItem } from '@/mastodon/models/dropdown_menu';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';
import HourglassIcon from '@/material-icons/400-24px/hourglass.svg?react';

// Self-destructing posts (bolha). Must match Status::ALLOWED_EXPIRATIONS on the server.
const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DURATIONS = [15 * MINUTE, 30 * MINUTE, ...[1, 2, 4, 6, 12, 24].map((hours) => hours * HOUR)];

const messages = defineMessages({
  title: {
    id: 'compose_form.expiration.title',
    defaultMessage: 'Self-destructing post',
  },
  activeHours: {
    id: 'compose_form.expiration.active',
    defaultMessage:
      'Deletes itself in {number, plural, one {# hour} other {# hours}}',
  },
  activeMinutes: {
    id: 'compose_form.expiration.active_minutes',
    defaultMessage:
      'Deletes itself in {number, plural, one {# minute} other {# minutes}}',
  },
  never: {
    id: 'compose_form.expiration.never',
    defaultMessage: 'Does not expire',
  },
  hours: {
    id: 'compose_form.expiration.hours',
    defaultMessage: 'Delete in {number, plural, one {# hour} other {# hours}}',
  },
  minutes: {
    id: 'compose_form.expiration.minutes',
    defaultMessage:
      'Delete in {number, plural, one {# minute} other {# minutes}}',
  },
});

const iconStyle = {
  lineHeight: '27px',
};

export const ExpirationButton: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const expiresIn = useAppSelector(
    (state) => state.compose.get('expires_in') as number | null,
  );

  const items = useMemo<MenuItem[]>(
    () => [
      {
        text: intl.formatMessage(messages.never),
        highlighted: expiresIn === null,
        action: () => {
          dispatch(changeComposeExpiration(null));
        },
      },
      null,
      ...DURATIONS.map((seconds) => ({
        text:
          seconds < HOUR
            ? intl.formatMessage(messages.minutes, { number: seconds / MINUTE })
            : intl.formatMessage(messages.hours, { number: seconds / HOUR }),
        highlighted: expiresIn === seconds,
        action: () => {
          dispatch(changeComposeExpiration(seconds));
        },
      })),
    ],
    [dispatch, intl, expiresIn],
  );

  let title = intl.formatMessage(messages.title);
  if (expiresIn !== null) {
    title =
      expiresIn < HOUR
        ? intl.formatMessage(messages.activeMinutes, {
            number: expiresIn / MINUTE,
          })
        : intl.formatMessage(messages.activeHours, {
            number: expiresIn / HOUR,
          });
  }

  return (
    <div className='compose-form__poll-button'>
      <Dropdown items={items} disabled={disabled} placement='bottom-start'>
        <IconButton
          icon='hourglass'
          iconComponent={HourglassIcon}
          title={title}
          disabled={disabled}
          active={expiresIn !== null}
          className={`compose-form__poll-button-icon ${expiresIn !== null ? 'active' : ''}`}
          inverted
          style={iconStyle}
        />
      </Dropdown>
    </div>
  );
};
